/**
 * Vouchers — Hospitality > Vouchers.
 *
 * Site-scoped guest access codes. Reuses the Meridian design system (md-card, Button,
 * Dialog, chips) and the GuestPortal list+dialog pattern. An operator generates a batch
 * (count + optional duration/label/prefix), hands out the printed codes, and revokes any
 * code. Codes enforce via the portal's "voucher" mode (captive credentials) — a hint is
 * shown when no portal is in voucher mode yet.
 *
 * State chips deliberately avoid the health ramp (reserved for telemetry): active/revoked/
 * expired render as neutral/brand-tinted chips, not StatusPill.
 */
import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useSession } from "@/stores/session";
import { useVouchers, useGenerateVouchers, useRevokeVoucher } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { parseApiTime, fmtAbsolute } from "@/lib/format";
import type { Voucher } from "@/types/domain";

// Duration presets (minutes); null = never expires.
const DURATIONS: { label: string; minutes: number | null }[] = [
  { label: "No expiry", minutes: null },
  { label: "1 hour", minutes: 60 },
  { label: "24 hours", minutes: 1440 },
  { label: "7 days", minutes: 10080 },
];

/** Neutral state chip — active is brand-tinted, revoked/expired are muted. */
function StateChip({ state }: { state: string }) {
  const cls =
    state === "active"
      ? "border-brand/30 bg-brand/10 text-brand"
      : "border-line bg-surface-2 text-ink-subtle";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide border ${cls}`}>
      {state}
    </span>
  );
}

function fmtExpiry(v: Voucher): string {
  if (v.state === "expired") return "expired";
  // parseApiTime pins the naive-UTC server timestamp to UTC so the local render is correct
  // (raw new Date() read it as local time -> voucher expiry off by the operator's offset).
  const t = parseApiTime(v.expires_at);
  if (t === null) return "no expiry";
  const d = new Date(t);
  return `until ${d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`;
}

export default function Vouchers() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = useVouchers(siteId);
  const [genOpen, setGenOpen] = useState(false);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Vouchers" subtitle="Guest Wi-Fi access codes" />
        <EmptyState
          title="Select a site first"
          hint="Vouchers belong to a site's guest Wi-Fi. Pick a site from the scope bar above."
        />
      </div>
    );
  }

  const vouchers = data?.vouchers ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="Vouchers" subtitle={siteName ? `Guest access codes in ${siteName}` : undefined} />
        <RoleGate min="site_operator" mode="hide">
          <Button variant="primary" onClick={() => setGenOpen(true)}>
            Generate vouchers
          </Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading vouchers…</div>}

      {data && (
        <div className="mb-3 flex items-center gap-4 text-sm text-ink-muted">
          <span>
            <span className="font-semibold text-ink">{data.active}</span> active
          </span>
          <span>
            <span className="font-semibold text-ink">{data.total}</span> total
          </span>
        </div>
      )}

      {data && vouchers.length === 0 && (
        <EmptyState
          title="No vouchers yet"
          hint="Generate a batch of codes, then attach them by setting a Guest Portal to Voucher mode."
        />
      )}

      {vouchers.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {vouchers.map((v) => (
            <li key={v.code} className="md-card flex items-center gap-3 p-2.5">
              <span className="font-mono text-sm font-semibold tracking-wider text-ink">{v.code}</span>
              <StateChip state={v.state} />
              <div className="ml-1 flex min-w-0 flex-1 flex-wrap gap-x-3 text-xs text-ink-muted">
                {v.label && <span className="truncate">{v.label}</span>}
                <span>{fmtExpiry(v)}</span>
                {v.data_cap_mb != null && <span>{v.data_cap_mb} MB cap</span>}
              </div>
              {v.state === "active" && (
                <RoleGate min="site_operator" mode="hide">
                  <RevokeButton siteId={siteId} code={v.code} />
                </RoleGate>
              )}
            </li>
          ))}
        </ul>
      )}

      {genOpen && <GenerateDialog siteId={siteId} siteName={siteName} onClose={() => setGenOpen(false)} />}
    </div>
  );
}

function RevokeButton({ siteId, code }: { siteId: string; code: string }) {
  const revoke = useRevokeVoucher(siteId);
  const [confirm, setConfirm] = useState(false);
  if (!confirm) {
    return (
      <Button variant="ghost" onClick={() => setConfirm(true)}>
        Revoke
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Button variant="danger" disabled={revoke.isPending} onClick={() => revoke.mutate(code)}>
        {revoke.isPending ? "…" : "Confirm"}
      </Button>
      <Button variant="ghost" onClick={() => setConfirm(false)}>
        Cancel
      </Button>
    </div>
  );
}

function GenerateDialog({
  siteId,
  siteName,
  onClose,
}: {
  siteId: string;
  siteName?: string;
  onClose: () => void;
}) {
  const gen = useGenerateVouchers(siteId);
  const [count, setCount] = useState("10");
  const [durationIdx, setDurationIdx] = useState(2); // default 24h
  const [label, setLabel] = useState("");
  const [prefix, setPrefix] = useState("");

  const newCodes = useMemo(() => gen.data?.vouchers.map((v) => v.code) ?? [], [gen.data]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number(count);
    if (!Number.isInteger(n) || n < 1 || n > 500) return;
    gen.mutate({
      count: n,
      duration_minutes: DURATIONS[durationIdx]?.minutes ?? null,
      label: label.trim() || undefined,
      prefix: prefix.trim() || undefined,
    });
  };

  const copyAll = () => {
    void navigator.clipboard?.writeText(newCodes.join("\n"));
  };

  // Open a clean printable window with the codes (works in the real app; no app-wide print CSS).
  // All interpolated values are HTML-escaped (site name is operator-controlled -> XSS guard).
  const printCodes = () => {
    const w = window.open("", "_blank", "width=480,height=640");
    if (!w) return;
    const esc = (s: string) =>
      s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
    const heading = "Guest Wi-Fi vouchers" + (siteName ? " — " + esc(siteName) : "");
    const rows = (gen.data?.vouchers ?? [])
      .map(
        (v) =>
          `<div class="v"><span class="c">${esc(v.code)}</span><span class="m">${
            v.expires_at ? esc("valid until " + fmtAbsolute(v.expires_at)) : "no expiry"
          }</span></div>`,
      )
      .join("");
    w.document.write(
      `<!doctype html><meta charset="utf-8"><title>${heading}</title><style>
        body{font:14px/1.4 -apple-system,Segoe UI,Roboto,sans-serif;margin:24px;color:#111}
        h1{font-size:15px;margin:0 0 12px}
        .v{display:flex;justify-content:space-between;align-items:baseline;border:1px solid #ddd;border-radius:6px;padding:8px 12px;margin:6px 0;break-inside:avoid}
        .c{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:18px;font-weight:700;letter-spacing:.12em}
        .m{font-size:11px;color:#666}
      </style><h1>${heading}</h1>${rows}
      <script>window.onload=function(){window.print()}<\/script>`,
    );
    w.document.close();
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Generate vouchers</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Codes are usable once a Guest Portal on this site is set to Voucher mode.
          </Dialog.Description>

          {newCodes.length === 0 ? (
            <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">How many</span>
                  <input
                    inputMode="numeric"
                    value={count}
                    onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, ""))}
                    className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Valid for</span>
                  <select
                    value={durationIdx}
                    onChange={(e) => setDurationIdx(Number(e.target.value))}
                    className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
                  >
                    {DURATIONS.map((d, i) => (
                      <option key={d.label} value={i}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Label (optional)</span>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Front desk, Room 214, Conference…"
                  className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Code prefix (optional)</span>
                <input
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                  placeholder="LOBBY"
                  maxLength={8}
                  className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                />
              </label>

              {gen.isError && <span className="text-xs text-critical">Could not generate the batch.</span>}
              <div className="mt-1 flex items-center justify-end gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={gen.isPending}>
                  {gen.isPending ? "Generating…" : "Generate"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              <div className="text-sm text-ink">
                {gen.data?.count} code{gen.data?.count === 1 ? "" : "s"} generated
                {gen.data && gen.data.push.pushed > 0 && (
                  <span className="text-ink-muted"> · pushed to {gen.data.push.pushed} AP{gen.data.push.pushed === 1 ? "" : "s"}</span>
                )}
              </div>
              <div className="max-h-56 overflow-y-auto rounded border border-line bg-surface-2 p-2">
                <div className="grid grid-cols-2 gap-1">
                  {newCodes.map((c) => (
                    <span key={c} className="font-mono text-sm font-semibold tracking-wider text-ink">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="secondary" onClick={copyAll}>
                  Copy all
                </Button>
                <Button variant="secondary" onClick={printCodes}>
                  Print
                </Button>
                <Button variant="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
